/**
 * StockMovementHistory Component
 *
 * Timeline/list component showing recent stock movements.
 * Displays batch receipts (received) and usage records (consumed).
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 4: Stock Overview Dashboard
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowDownCircle, ArrowUpCircle, Package, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { parseDateString } from '../../utils/dateUtils'

/**
 * Format a date for display
 *
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date string
 */
function formatDate(dateString) {
  if (!dateString) return ''

  const date = parseDateString(dateString)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())

  if (dateOnly.getTime() === today.getTime()) {
    return 'Today'
  }
  if (dateOnly.getTime() === yesterday.getTime()) {
    return 'Yesterday'
  }

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  })
}

/**
 * Format quantity with unit
 *
 * @param {number} quantity - Quantity value
 * @param {string} unit - Unit of measure
 * @returns {string} - Formatted quantity string
 */
function formatQuantity(quantity, unit) {
  const formatted = Number.isInteger(quantity) ? quantity.toString() : quantity.toFixed(1)
  return `${formatted}${unit ? ` ${unit}` : ''}`
}

/**
 * Single movement item in the timeline
 */
function MovementItem({ movement }) {
  const isReceived = movement.type === 'received'
  const Icon = isReceived ? ArrowDownCircle : ArrowUpCircle

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-b-0">
      {/* Icon */}
      <div
        className={cn(
          'flex-shrink-0 p-2 rounded-full',
          isReceived ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-foreground">
              {movement.description}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {movement.batchCode && (
                <span className="font-mono">{movement.batchCode}</span>
              )}
              {movement.supplierName && (
                <span className="ml-2">from {movement.supplierName}</span>
              )}
            </p>
          </div>

          {/* Quantity badge */}
          <Badge
            variant="outline"
            className={cn(
              'flex-shrink-0',
              isReceived
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-blue-50 text-blue-700 border-blue-200'
            )}
          >
            {isReceived ? '+' : '-'}{formatQuantity(movement.quantity, movement.unit)}
          </Badge>
        </div>
      </div>

      {/* Date */}
      <div className="flex-shrink-0 text-xs text-muted-foreground">
        {formatDate(movement.date)}
      </div>
    </div>
  )
}

/**
 * Empty state when no movements
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Clock className="h-10 w-10 text-muted-foreground/50 mb-3" />
      <p className="text-sm text-muted-foreground">
        No recent stock movements
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        Record a purchase to see activity here
      </p>
    </div>
  )
}

/**
 * StockMovementHistory Component
 *
 * @param {Object} props
 * @param {Array} props.batches - Array of inventory batches (from useInventoryBatches)
 * @param {number} props.limit - Maximum number of movements to display (default: 10)
 * @param {string} props.className - Additional CSS classes
 */
export default function StockMovementHistory({
  batches = [],
  limit = 10,
  className = ''
}) {
  // Transform batches into movement entries
  // For now, we only show "received" movements from batch receipts
  // Future: Include usage records when consuming materials
  const movements = batches
    .slice(0, limit)
    .map(batch => ({
      id: batch.id,
      type: 'received',
      description: `${batch.item_name || 'Item'} received`,
      batchCode: batch.batch_code,
      supplierName: batch.supplier_name,
      quantity: batch.quantity_received || 0,
      unit: batch.unit_of_measure || '',
      date: batch.purchase_date || batch.created_at
    }))

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="h-4 w-4" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {movements.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="divide-y divide-border">
            {movements.map((movement) => (
              <MovementItem key={movement.id} movement={movement} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
